import React, { useState, useCallback, useRef } from 'react';
import { FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaLinkedin, FaTwitter, FaInstagram, FaYoutube, FaComments, FaQuestionCircle, FaTimes } from 'react-icons/fa';
import Navbar from './Navbar';
import Footer from './Footer';
import FAQ from './FAQ';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';

const ContactItem = ({ icon, text, link, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
  >
    <div className="text-orange-500">{icon}</div>
    <div>
      {link ? (
        <a
          href={link}
          className="text-gray-800 hover:text-orange-500 transition-colors duration-200 font-medium"
        >
          {text}
        </a>
      ) : (
        <p className="text-gray-800 font-medium">{text}</p>
      )}
      {description && (
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      )}
    </div>
  </motion.div>
);

const SocialLink = ({ href, icon, label }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <div className="text-gray-600 group-hover:text-orange-500 transition-colors duration-200">
      {icon}
    </div>
    <span className="text-sm text-gray-600 mt-2 group-hover:text-orange-500 transition-colors duration-200">
      {label}
    </span>
  </motion.a>
);

const SupportCard = ({ icon, title, description, action, actionIcon, actionText }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl shadow-lg p-4 md:p-8 hover:shadow-xl transition-shadow duration-200"
  >
    <div className="flex items-center space-x-4 mb-6">
      <div className="p-3 bg-orange-100 rounded-xl">
        {icon}
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-gray-800">{title}</h3>
    </div>
    <p className="text-gray-600 text-sm md:text-lg mb-6">{description}</p>
    <button
      onClick={action}
      className="w-full flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white py-3 px-6 rounded-xl shadow-md transition-all duration-300 transform hover:scale-[1.02]"
    >
      {actionIcon}
      <span className="ml-2">{actionText}</span>
    </button>
  </motion.div>
);

const ComingSoonDialog = ({ isOpen, onClose, title, icon }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4"
        >
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-3">
              <div className="text-orange-500">{icon}</div>
              <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
          <div className="text-center py-8">
            <div className="text-6xl text-orange-500 mb-4">🚀</div>
            <h4 className="text-2xl font-bold text-gray-800 mb-2">Coming Soon!</h4>
            <p className="text-gray-600">
              We're working hard to bring you this feature. Stay tuned for updates!
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200"
            >
              Got it
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const ContactSupport = () => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLiveChatDialog, setShowLiveChatDialog] = useState(false);
  const [showFAQDialog, setShowFAQDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'general',
    subject: '',
    message: ''
  });

  // Use ref to track active toast
  const activeToastRef = useRef(null);

  // Toast configuration
  const toastConfig = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    newestOnTop: true,
    closeButton: true,
  };

  // Custom toast function with proper cleanup
  const showToast = useCallback((message, type = 'success') => {
    // Clear any existing toast
    if (activeToastRef.current) {
      toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
    }

    // Show new toast
    const toastId = toast[type](message, {
      ...toastConfig,
      onClose: () => {
        if (activeToastRef.current === toastId) {
          activeToastRef.current = null;
        }
      }
    });

    activeToastRef.current = toastId;
    return toastId;
  }, []);

  const handleWhatsAppRedirect = () => {
    const phoneNumber = "919220174144";
    const whatsappURL = `https://api.whatsapp.com/send?phone=${phoneNumber}`;
    window.open(whatsappURL, "_blank", "noopener,noreferrer");
  };

  const handleLiveChat = () => {
    setShowLiveChatDialog(true);
  };

  const handleFAQ = () => {
    setShowFAQDialog(true);
  };

  const validateForm = () => {
    const { name, email, type, subject, message } = formData;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name.trim() || name.length < 3) {
      showToast('Name should be at least 3 characters.', 'error');
      return false;
    }

    if (!emailRegex.test(email)) {
      showToast('Please enter a valid email address.', 'error');
      return false;
    }

    if (!type || type === 'general') {
      showToast('Please select your role.', 'error');
      return false;
    }

    if (!subject.trim() || subject.length < 5) {
      showToast('Subject should be at least 5 characters.', 'error');
      return false;
    }

    if (!message.trim() || message.length < 10) {
      showToast('Message should be at least 10 characters.', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Clear any existing toast
    if (activeToastRef.current) {
      toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
    }
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("access_key", process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "71d20a3e-9360-4404-b33d-de3a16ff1d63");
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("type", formData.type);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        showToast('Your message has been sent successfully!', 'success');
        setFormData({ name: '', email: '', type: 'general', subject: '', message: '' });
      } else {
        console.error("Form submission error:", data);
        showToast(data.message || 'Something went wrong. Please try again.', 'error');
      }
    } catch (error) {
      console.error("Form submission error:", error);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-gray-100">
      <Navbar />
      <ToastContainer
        {...toastConfig}
        enableMultiContainer={false}
        containerId="contact-form-toasts"
        limit={1}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          {/* <h1 className="text-lg md:text-2xl font-bold mb-6">Connect With Infuber</h1> */}
          <p className="text-sm md:text-lg text-orange-100 max-w-2xl mx-auto">
            Whether you're a brand looking to collaborate with influencers or an influencer seeking opportunities, we're here to help you succeed.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 md:px-4 py-6 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Get in Touch</h1>
          <p className="text-gray-600 text-xs md:text-sm">
            We're here to help and answer any questions you might have
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-4 md:p-8"
            >
              <h2 className="text-base md:text-xl font-bold text-gray-800 mb-6">Contact Information</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs md:text-sm">
                {/* Email */}
                <ContactItem
                  icon={<FaEnvelope className="w-6 h-6" />}
                  text="help@infuber.com"
                  link="mailto:help@infuber.com"
                  description="For general inquiries and support"
                />

                {/* Phone Numbers */}
                <div className="space-y-4 text-xs md:text-sm">
                  <ContactItem
                    icon={<FaPhone className="w-6 h-6" />}
                    text="+91 922 017 4144"
                    description="India Support"
                  />
                  <ContactItem
                    icon={<FaPhone className="w-6 h-6" />}
                    text="+44 7713709034"
                    description="UK Support"
                  />
                </div>

                {/* Addresses */}
                <div className="space-y-4 text-xs md:text-sm">
                  <ContactItem
                    icon={<FaMapMarkerAlt className="w-6 h-6" />}
                    text="Golf Course Rd, DLF Phase 5, Sector 54, Gurugram, Haryana, India"
                    description="Headquarters"
                  />
                  <ContactItem
                    icon={<FaMapMarkerAlt className="w-6 h-6" />}
                    text="Hamilton house, 8 victory place, london E14 8BQ, UK"
                    description="European Office"
                  />
                </div>

                {/* Support Timings */}
                <div className="space-y-4 text-xs md:text-sm">
                  <ContactItem
                    icon={<FaClock className="w-6 h-6" />}
                    text="Mon - Fri: 9:00 AM - 6:00 PM IST"
                    description="India Office Hours"
                  />
                  <ContactItem
                    icon={<FaClock className="w-6 h-6" />}
                    text="Mon - Fri: 9:00 AM - 6:00 PM BST"
                    description="UK Office Hours"
                  />
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-10">
                <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-4">Connect With Us</h3>
                <div className="grid grid-cols-4 gap-4">
                  <SocialLink
                    href="https://www.instagram.com/infuberoriginal/"
                    icon={<FaInstagram className="w-6 h-6" />}
                    label="Instagram"
                  />
                  <SocialLink
                    href="https://www.youtube.com/channel/UCQhvBbMUy67OAsmynFjp_gQ"
                    icon={<FaYoutube className="w-6 h-6" />}
                    label="YouTube"
                  />
                  <SocialLink
                    href="https://x.com/infuber"
                    icon={<FaTwitter className="w-6 h-6" />}
                    label="Twitter"
                  />
                  <SocialLink
                    href="https://www.linkedin.com/company/infuber/"
                    icon={<FaLinkedin className="w-6 h-6" />}
                    label="LinkedIn"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Support Options */}
          <div className="space-y-8 text-xs md:text-sm">
            <SupportCard
              icon={<FaWhatsapp className="w-4 h-4 md:w-6 md:h-6 text-orange-500" />}
              title="WhatsApp Support"
              description="Get instant support through WhatsApp. Our team typically responds within minutes."
              action={handleWhatsAppRedirect}
              actionIcon={<FaWhatsapp className="w-5 h-5" />}
              actionText="Chat on WhatsApp"
            />

            <SupportCard
              icon={<FaComments className="w-4 h-4 md:w-6 md:h-6 text-orange-500" />}
              title="Live Chat"
              description="Chat with our support team in real-time for immediate assistance."
              action={handleLiveChat}
              actionIcon={<FaComments className="w-5 h-5" />}
              actionText="Start Live Chat"
            />

            <SupportCard
              icon={<FaQuestionCircle className="w-4 h-4 md:w-6 md:h-6 text-orange-500" />}
              title="FAQ Center"
              description="Find quick answers to common questions in our FAQ section."
              action={handleFAQ}
              actionIcon={<FaQuestionCircle className="w-5 h-5" />}
              actionText="View FAQs"
            />
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="max-w-7xl mx-auto px-2 md:px-4 py-6 md:py-12">
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8">
          <h2 className="text-sm md:text-lg font-bold text-gray-800 mb-6">Send us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-2 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-2 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                required
              />
            </div>
            <div>
              <label htmlFor="type" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                I am a
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-2 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                required
              >
                <option value="general">Select your role</option>
                <option value="brand">Brand/Company</option>
                <option value="influencer">Influencer/Creator</option>
                <option value="agency">Agency</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="subject" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-2 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                required
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-2 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                required
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-orange-500 text-white py-2 md:py-3 px-2 md:px-6 rounded-xl shadow-md transition-all duration-300 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-orange-600'
                }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>

      {/* Live Chat Dialog */}
      <ComingSoonDialog
        isOpen={showLiveChatDialog}
        onClose={() => setShowLiveChatDialog(false)}
        title="Live Chat"
        icon={<FaComments className="w-6 h-6" />}
      />

      {/* FAQ Dialog */}
      <FAQ
        isOpen={showFAQDialog}
        onClose={() => setShowFAQDialog(false)}
      />

      <Footer />
    </div>
  );
};

export default ContactSupport;


